import type {
  ArticleCtaRoute,
  ArticleStatus,
  ArticleType,
} from "@care-by-ella/contracts";
import { ARTICLE_CTA_ROUTES, ARTICLE_TYPES } from "@care-by-ella/contracts";

// CAR-119/120 — Swedish is the product-approved base copy for the article
// editor; English is derived from it. These labels are clinician-facing chrome
// only (the article content itself is tenant-authored), so they live here
// rather than in @care-by-ella/contracts.

export const ARTICLE_TYPE_LABELS: Record<ArticleType, string> = {
  mini_lesson: "Minilektion",
  checklist: "Checklista",
  education_card: "Utbildningskort",
  coach_message: "Coachmeddelande",
  article: "Artikel",
  clinical_result: "Klinisk resultatvy (CP-09)",
  recipe: "Recept (Stöd → Kost)",
  nutrition_plan: "Kostupplägg (Stöd → Kost)",
  training_pass: "Träningspass (Stöd → Träning)",
  training_exercise: "Träningsövning (Stöd → Träning)",
  support_copy: "Stöd-sektionscopy",
  short_guide: "Kort guide (Stöd)",
  coach_template: "Coachmall",
};

// TES-1355 — coach templates have their own page (Coachmallar) and the
// generic article routes refuse them, so the content editor never offers
// the type.
export const ARTICLE_TYPE_ORDER: readonly ArticleType[] = ARTICLE_TYPES.filter(
  (t) => t !== "coach_template",
);

export const ARTICLE_STATUS_LABELS: Record<ArticleStatus, string> = {
  draft: "Utkast",
  published: "Publicerad",
};

// Human labels for the in-app destinations a CTA button can deep-link to.
// Clinician-facing chrome (the route ids are mobile AppStack names), so they
// live here rather than in contracts. The "no route" option renders an inert,
// non-navigating button in the app.
export const ARTICLE_CTA_ROUTE_LABELS: Record<ArticleCtaRoute, string> = {
  FindLab: "Hitta labb",
  WeeklyCheckin: "Veckoavstämning",
  Provsvar: "Provsvar",
  Chatt: "Chatt",
  // TES-1483 (Ö-641) — the route now opens "Din hälsa över tid". Only an
  // article that already carries it still shows it; see articleCtaRouteOptions.
  ThenVsNow: "Din hälsa över tid (tidigare Förr och nu)",
  FirstDose: "Registrera första dosen",
  RecordBloodPressure: "Registrera blodtryck och vilopuls",
};

// TES-1483 (Ö-641) — routes that stay valid for published articles and old
// builds but that a new button can no longer pick. The contract enum keeps
// them, so existing articles still validate.
export const RETIRED_ARTICLE_CTA_ROUTES: readonly ArticleCtaRoute[] = [
  "ThenVsNow",
];

// The destinations offered in the editor's picker: every live route, plus
// the article's own current route when it is a retired one, so opening and
// saving an old article neither hides nor silently drops its button target.
export function articleCtaRouteOptions(
  current: ArticleCtaRoute | "",
): ArticleCtaRoute[] {
  return ARTICLE_CTA_ROUTES.filter(
    (route) => !RETIRED_ARTICLE_CTA_ROUTES.includes(route) || route === current,
  );
}

// Maps the API's machine `error` code to clinician-facing Swedish copy.
export function articleErrorMessage(code: string): string {
  switch (code) {
    case "article_key_taken":
      return "Nyckeln används redan. Välj en annan nyckel.";
    case "article_not_found":
      return "Artikeln kunde inte hittas.";
    case "validation_failed":
      return "Innehållet kunde inte sparas. Kontrollera fälten.";
    default:
      return "Något gick fel. Försök igen.";
  }
}
